import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Draggable } from 'react-beautiful-dnd';
import {
  MdAttachFile,
  MdBusiness,
  MdChevronRight,
  MdDeleteOutline,
  MdDescription,
  MdDragIndicator,
  MdEdit,
  MdEmail,
  MdExpandLess,
  MdExpandMore,
  MdFolder,
  MdForum,
  MdHistory,
  MdLabel,
  MdLink,
  MdLocationOn,
  MdLock,
  MdLockOpen,
  MdMoreHoriz,
  MdOutlineSendToMobile,
  MdOutlineTaskAlt,
  MdPrecisionManufacturing,
  MdSchedule,
  MdShare,
  MdShoppingCart,
  MdStar,
  MdThermostat,
  MdThumbDown,
  MdThumbUp,
  MdViewColumn,
  MdWhatsapp,
} from 'react-icons/md';
import {
  formatCurrency,
  getCardDocumentSummary,
  getCardLocation,
  getDaysSinceUpdate,
  getStatusTone,
} from '../utils';
import NewAvatar from './NewAvatar';

const Detail = ({ icon: Icon, label, value, wide = false, controls = [] }) => (
  <div className={`new-home-card-detail ${wide ? 'is-wide' : ''}`} title={String(value || '')}>
    <Icon />
    <span><small>{label}</small><strong>{value || 'Não informado'}</strong></span>
    {controls.length > 0 && (
      <div className="new-home-card-detail-actions">
        {controls.map(({ key, icon: ControlIcon, label: controlLabel, onClick, tone = '' }) => (
          <button key={key} className={tone ? `is-${tone}` : ''} type="button" title={controlLabel} aria-label={controlLabel} onClick={onClick}>
            <ControlIcon aria-hidden="true" />
          </button>
        ))}
      </div>
    )}
  </div>
);

function NewKanbanCard({ card, index, actions, density }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [labelOpen, setLabelOpen] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [detailsLoaded, setDetailsLoaded] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState(false);
  const [menuPosition, setMenuPosition] = useState(null);
  const tone = getStatusTone(card);
  const owner = actions.getOwner(card.entity_id);
  const label = actions.labels.find((item) => Number(item.id) === Number(card.etiqueta_id));
  const rating = Number(card.potencial_venda || 0);
  const rawStatus = typeof card.status === 'string' ? card.status.trim() : '';
  const currentStatus = rawStatus && rawStatus !== '0' && rawStatus !== 'Novo' ? rawStatus : '';
  const statusLabel = currentStatus || 'Aberto';
  const statusStyle = currentStatus === 'Vendido' ? 'is-success' : currentStatus === 'Perdido' ? 'is-danger' : currentStatus === 'Arquivado' ? 'is-archived' : 'is-open';
  const canEditColumn = actions.editableColumnIds.includes(Number(card.column_id));
  const daysSinceUpdate = getDaysSinceUpdate(card.updated_at || card.created_at);
  const inactivityLabel = daysSinceUpdate === 0
    ? 'Atualizado hoje'
    : `Há ${daysSinceUpdate} ${daysSinceUpdate === 1 ? 'dia' : 'dias'} sem atualização`;
  const cardLocation = getCardLocation(card);
  const cardLocationValue = cardLocation === 'Local não informado' ? 'Não informado' : cardLocation;
  const cardOrigin = card.origem || 'Não informada';
  const cardDocument = getCardDocumentSummary(card);
  const displayValue = (value) => !actions.canViewValue ? 'Restrito' : actions.showValues ? formatCurrency(value) : '••••••';
  const valueTitle = (value) => !actions.canViewValue ? 'Restrito' : actions.showValues ? formatCurrency(value) : 'Valor oculto';
  const columnOptions = actions.columns.some((item) => Number(item.id) === Number(card.column_id))
    ? actions.columns
    : [{ id: card.column_id, name: actions.getColumnName(card.column_id) }, ...actions.columns];

  const run = (callback) => {
    setMenuOpen(false);
    setLabelOpen(false);
    setStatusOpen(false);
    callback(card);
  };

  const runFromControl = (event, callback) => {
    event.stopPropagation();
    run(callback);
  };

  const toggleExpanded = async (event) => {
    event?.stopPropagation();
    const nextExpanded = !expanded;
    setExpanded(nextExpanded);
    setMenuOpen(false);
    setLabelOpen(false);
    setStatusOpen(false);

    if (!nextExpanded || detailsLoaded || detailsLoading || !actions.loadDetails) return;
    setDetailsLoading(true);
    setDetailsError(false);
    try {
      await actions.loadDetails(card);
      setDetailsLoaded(true);
    } catch (error) {
      setDetailsError(true);
    } finally {
      setDetailsLoading(false);
    }
  };

  const detailsId = `new-home-card-details-${card.card_id}`;
  const menuPortalTarget = typeof document !== 'undefined' ? document.querySelector('.new-home') : null;

  useEffect(() => {
    if (!menuOpen) return undefined;
    const closeMenu = () => setMenuOpen(false);
    window.addEventListener('resize', closeMenu);
    document.addEventListener('scroll', closeMenu, true);
    return () => {
      window.removeEventListener('resize', closeMenu);
      document.removeEventListener('scroll', closeMenu, true);
    };
  }, [menuOpen]);

  const toggleMenu = (event) => {
    event.stopPropagation();
    setLabelOpen(false);
    setStatusOpen(false);
    if (menuOpen) { setMenuOpen(false); return; }

    const rect = event.currentTarget.getBoundingClientRect();
    const menuWidth = 236;
    const left = Math.min(Math.max(8, rect.right - menuWidth), window.innerWidth - menuWidth - 8);
    const roomBelow = window.innerHeight - rect.bottom - 12;
    setMenuPosition(roomBelow >= 330
      ? { left, top: rect.bottom + 6, maxHeight: roomBelow }
      : { left, top: 'auto', bottom: window.innerHeight - rect.top + 6, maxHeight: Math.max(190, rect.top - 14) });
    setMenuOpen(true);
  };

  return (
    <Draggable draggableId={JSON.stringify({ cardData: card, block_column: card.block_column })} index={index} isDragDisabled={!canEditColumn}>
      {(provided, snapshot) => (
        <article
          className={`new-home-card tone-${tone} density-${density} ${expanded ? 'is-expanded' : ''} ${snapshot.isDragging ? 'is-dragging' : ''} ${!canEditColumn ? 'is-locked' : ''} ${menuOpen || labelOpen || statusOpen ? 'has-open-popover' : ''}`}
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          onClick={toggleExpanded}
          tabIndex={0}
          onKeyDown={(event) => {
            if (event.target !== event.currentTarget) return;
            if (event.key === 'Enter' || event.key === ' ') toggleExpanded(event);
            if (event.key === 'Escape') { setMenuOpen(false); setLabelOpen(false); setStatusOpen(false); }
          }}
          aria-label={`${card.name || 'Card'}; ${!actions.canViewValue ? 'valor restrito' : actions.showValues ? formatCurrency(card.cost_value) : 'valor oculto'}`}
        >
          <div className="new-home-card-rail" style={{ background: label?.color || undefined }} />
          <div className="new-home-card-topline">
            <button
              className={`new-home-card-status-trigger ${statusStyle}`}
              type="button"
              title={`Status: ${statusLabel}`}
              aria-label={`Status do card: ${statusLabel}. Alterar status`}
              aria-expanded={statusOpen}
              disabled={!actions.canStatus}
              onClick={(event) => {
                event.stopPropagation();
                setMenuOpen(false);
                setLabelOpen(false);
                setStatusOpen((value) => !value);
              }}
            >
              {currentStatus === 'Vendido' && <MdThumbUp />}
              {currentStatus === 'Perdido' && <MdThumbDown />}
              {currentStatus === 'Arquivado' && <MdFolder />}
              {!['Vendido', 'Perdido', 'Arquivado'].includes(currentStatus) && <MdOutlineTaskAlt />}
              <span>{statusLabel}</span>
            </button>
            {label && <button className="new-home-card-label-dot" aria-label={`Etiqueta: ${label.description}`} title={label.description} style={{ '--label-color': label.color }} onClick={(event) => { event.stopPropagation(); setMenuOpen(false); setStatusOpen(false); setLabelOpen((value) => !value); }} />}
            {Number(card.temperatura_card) > 0 && <span className={`new-home-temperature temp-${card.temperatura_card}`} title="Temperatura do lead" />}
            <span className="new-home-card-spacer" />
            {card.block_column ? <MdLock className="new-home-lock-indicator" title="Movimentação restrita" /> : null}
            <span className="new-home-card-drag" aria-label={canEditColumn ? 'Arraste o card por qualquer ponto' : 'Sem permissão para mover'} title={canEditColumn ? 'Arraste o card por qualquer ponto' : 'Sem permissão para mover'}><MdDragIndicator /></span>
            <button className="new-home-card-expand" onClick={toggleExpanded} aria-label={expanded ? 'Recolher detalhes do card' : 'Expandir detalhes do card'} aria-expanded={expanded} title={expanded ? 'Modo simples' : 'Modo expandido'}>
              {expanded ? <MdExpandLess /> : <MdExpandMore />}
            </button>
            <button className="new-home-icon-button" onClick={toggleMenu} aria-label="Ações do card" aria-expanded={menuOpen}><MdMoreHoriz /></button>
          </div>

          <div className="new-home-card-main" title={card.name || 'Cliente sem nome'}><h3>{card.name || 'Cliente sem nome'}</h3></div>

          <div className="new-home-card-value-row">
            <div><small>Valor</small><strong title={valueTitle(card.cost_value)}>{displayValue(card.cost_value)}</strong></div>
            {Boolean(card.sale_value) && actions.canViewValue && <div className="new-home-card-sale"><small>Venda</small><span title={valueTitle(card.sale_value)}>{displayValue(card.sale_value)}</span></div>}
          </div>

          <div className="new-home-card-meta">
            <Detail icon={MdLocationOn} label="Local" value={cardLocationValue} />
            <Detail icon={MdChevronRight} label="Origem" value={cardOrigin} />
            <Detail icon={MdDescription} label="Documento" value={cardDocument} wide />
          </div>

          {expanded && (
            <section id={detailsId} className="new-home-card-details" aria-label="Detalhes do card" onClick={(event) => event.stopPropagation()}>
              <div className="new-home-card-details-heading">
                <strong>Detalhes</strong>
                {detailsLoading && <span>Atualizando…</span>}
                {!detailsLoading && detailsError && <span className="is-error">Não foi possível atualizar</span>}
              </div>

              <div className="new-home-card-field-grid">
                <label><span><MdLabel />Etiqueta</span><select value={card.etiqueta_id || ''} onChange={(event) => actions.label(card, event.target.value ? Number(event.target.value) : null)} disabled={!actions.canLabel}><option value="">Sem etiqueta</option>{actions.labels.map((item) => <option key={item.id} value={item.id}>{item.description}</option>)}</select></label>
                <div className="new-home-card-field-item">
                  <span><MdViewColumn />Coluna</span>
                  <div className="new-home-card-field-control">
                    <select aria-label="Coluna do card" value={card.column_id} onChange={(event) => actions.move(card, Number(event.target.value))} disabled={!canEditColumn}>{columnOptions.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select>
                    {actions.canLock && (
                      <button type="button" title={card.block_column ? 'Desbloquear card' : 'Bloquear card nesta coluna'} aria-label={card.block_column ? 'Desbloquear card' : 'Bloquear card nesta coluna'} onClick={(event) => runFromControl(event, () => actions.lock(card, !card.block_column))}>
                        {card.block_column ? <MdLock /> : <MdLockOpen />}
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <div className="new-home-card-details-grid">
                <Detail
                  icon={MdDescription}
                  label="Orçamento"
                  value={card.document_number}
                  controls={actions.canDelete ? [{ key: 'delete', icon: MdDeleteOutline, label: 'Excluir card permanentemente', tone: 'danger', onClick: (event) => runFromControl(event, () => actions.remove(card)) }] : []}
                />
                {Boolean(card.second_document_number) && <Detail icon={MdDescription} label="Orçamento Gest" value={card.second_document_number} />}
                <Detail icon={MdShoppingCart} label="Pedido" value={card.pedido_number} />
                <Detail icon={MdBusiness} label="Obra" value={card.nome_obra} wide />
                {actions.canContact && <Detail icon={MdWhatsapp} label="Telefone" value={card.fone} controls={[{ key: 'whatsapp', icon: MdWhatsapp, label: 'Iniciar conversa pelo WhatsApp', tone: 'success', onClick: (event) => runFromControl(event, () => actions.directWhatsapp(card)) }]} />}
                {actions.canContact && <Detail icon={MdEmail} label="E-mail" value={card.email} wide />}
                <Detail icon={MdLocationOn} label="Localização" value={getCardLocation(card)} wide />
                <Detail icon={MdBusiness} label="Produto" value={card.produto} />
                <Detail icon={MdChevronRight} label="Origem" value={card.origem} controls={actions.canNotifyPartner ? [{ key: 'partner', icon: MdOutlineSendToMobile, label: 'Enviar mensagem para o parceiro', onClick: (event) => runFromControl(event, () => actions.notifyPartner(card)) }] : []} />
                {Boolean(card.utm_campaign) && <Detail icon={MdChevronRight} label="Campanha" value={card.utm_campaign} wide />}
                {Boolean(card.utm_content) && <Detail icon={MdChevronRight} label="Conjunto" value={card.utm_content} wide />}
                {Boolean(card.utm_term) && <Detail icon={MdChevronRight} label="Anúncio" value={card.utm_term} wide />}
                <Detail icon={MdViewColumn} label="Valor de custo" value={displayValue(card.cost_value)} />
                <Detail icon={MdShoppingCart} label="Valor de venda" value={displayValue(card.sale_value)} />
                <Detail
                  icon={MdPrecisionManufacturing}
                  label="Etapa produção"
                  value={Number(card.etapa_producao || 0) + 1}
                  controls={[
                    actions.canProductionStage && { key: 'stage', icon: MdPrecisionManufacturing, label: 'Criar etapa de produção', onClick: (event) => runFromControl(event, () => actions.productionStage(card)) },
                    Boolean(card.card_original_id) && { key: 'original', icon: MdHistory, label: 'Abrir card original', onClick: (event) => runFromControl(event, () => actions.original(card)) },
                  ].filter(Boolean)}
                />
              </div>

              <div className="new-home-card-module-section">
                <strong>Módulos</strong>
                <div className="new-home-card-module-grid">
                  {actions.canEdit && <button title="Editar card" aria-label="Editar card" onClick={(event) => runFromControl(event, () => actions.open('edit', card))}><MdEdit /><span>Editar</span></button>}
                  {actions.canHistory && <button title="Histórico geral" aria-label="Histórico geral" onClick={(event) => runFromControl(event, () => actions.open('history', card))}><MdHistory /><span>Histórico</span></button>}
                  {actions.canTasks && <button title="Criar ou editar tarefas" aria-label="Criar ou editar tarefas" onClick={(event) => runFromControl(event, () => actions.open('tasks', card))}><MdOutlineTaskAlt /><span>Tarefas</span></button>}
                  {actions.canShare && <button title="Compartilhar card" aria-label="Compartilhar card" onClick={(event) => runFromControl(event, () => actions.open('share', card))}><MdShare /><span>Compartilhar</span></button>}
                  {actions.canContact && <button title="Abrir Messenger" aria-label="Abrir Messenger" onClick={(event) => runFromControl(event, () => actions.chat(card))}><MdForum /><span>Messenger</span></button>}
                  <button title="Documentos e relatórios" aria-label="Documentos e relatórios" onClick={(event) => runFromControl(event, () => actions.open('documents', card))}><MdDescription /><span>Documentos</span></button>
                </div>
              </div>

              <div className="new-home-card-module-section">
                <strong>Módulos extras</strong>
                <div className="new-home-card-module-grid">
                  {actions.canContact && <button title="Abrir WhatsApp" aria-label="Abrir WhatsApp" onClick={(event) => runFromControl(event, () => actions.whatsapp(card))}><MdWhatsapp /><span>WhatsApp</span></button>}
                  {actions.canProduction && <button title="Processo de produção" aria-label="Processo de produção" onClick={(event) => runFromControl(event, () => actions.open('production', card))}><MdBusiness /><span>Produção</span></button>}
                  {actions.canAttachments && <button title="Arquivos anexados" aria-label="Arquivos anexados" onClick={(event) => runFromControl(event, () => actions.open('attachments', card))}><MdAttachFile /><span>Anexos</span></button>}
                  <button title="Enviar e-mail" aria-label="Enviar e-mail" onClick={(event) => runFromControl(event, () => actions.open('email', card))}><MdEmail /><span>E-mail</span></button>
                  {actions.canOrders && <button title="Formulário de pedidos" aria-label="Formulário de pedidos" onClick={(event) => runFromControl(event, () => actions.open('orders', card))}><MdShoppingCart /><span>Pedidos</span></button>}
                  <button title="Copiar link de acompanhamento" aria-label="Copiar link de acompanhamento" onClick={(event) => runFromControl(event, () => actions.tracking(card))}><MdLink /><span>Acompanhamento</span></button>
                  {actions.canFaktory && <button title="Abrir Faktory" aria-label="Abrir Faktory" onClick={(event) => runFromControl(event, () => actions.faktory(card))}><MdBusiness /><span>Faktory</span></button>}
                  {actions.canProductionStage && <button title="Criar etapa de produção" aria-label="Criar etapa de produção" onClick={(event) => runFromControl(event, () => actions.productionStage(card))}><MdPrecisionManufacturing /><span>Nova etapa</span></button>}
                  {Boolean(card.card_original_id) && <button title="Abrir card original" aria-label="Abrir card original" onClick={(event) => runFromControl(event, () => actions.original(card))}><MdHistory /><span>Card original</span></button>}
                </div>
              </div>

              {actions.canTemperature && <div className="new-home-card-module-section"><strong>Marketing</strong><div className="new-home-card-temperature-actions">{[['Frio', 1], ['Morno', 2], ['Quente', 3]].map(([name, value]) => <button key={value} className={Number(card.temperatura_card) === value ? 'is-active' : ''} onClick={(event) => runFromControl(event, () => actions.temperature(card, value))}><MdThermostat />{name}</button>)}</div></div>}
              {card.status === 'Perdido' && <div className="new-home-card-reason"><strong>Motivo da perda</strong><span>{card.motivo_venda_perdida || 'Não informado'}</span></div>}
              {card.status === 'Arquivado' && <div className="new-home-card-reason"><strong>Motivo por arquivar</strong><span>{card.motivo_card_arquivado || 'Não informado'}</span></div>}
            </section>
          )}

          <div className="new-home-card-summary-row">
            <span className="new-home-card-days" title={inactivityLabel} aria-label={inactivityLabel}>
              <MdSchedule aria-hidden="true" />
              <span>{inactivityLabel}</span>
            </span>
          </div>

          <footer className="new-home-card-footer">
            <span className="new-home-card-owner" title={owner?.username || card.responsavel || 'Responsável não identificado'}>
              <NewAvatar avatar={owner?.avatar} alt="" />
              {density !== 'compact' && <small>{owner?.username || card.responsavel || 'Sem responsável'}</small>}
              {expanded && <button className="new-home-card-owner-alert" type="button" title="Enviar e-mail de aviso ao responsável" aria-label="Enviar e-mail de aviso ao responsável" onClick={(event) => runFromControl(event, () => actions.notifyOwner(card))}>!</button>}
            </span>
            <div className="new-home-card-rating" aria-label={`Potencial de venda: ${rating} de 5`}>
              {[1, 2, 3, 4, 5].map((value) => (
                <button key={value} onClick={(event) => { event.stopPropagation(); actions.rate(card, value); }} aria-label={`${value} estrelas`} disabled={!actions.canRate}><MdStar className={value <= rating ? 'is-filled' : ''} /></button>
              ))}
            </div>
            <button onClick={(event) => runFromControl(event, () => actions.open('tasks', card))} title="Tarefas" aria-label="Abrir tarefas" disabled={!actions.canTasks}><MdOutlineTaskAlt />{card.task_count || card.tasks_count || ''}</button>
          </footer>

          {labelOpen && (
            <div className="new-home-card-popover new-home-label-popover" onClick={(event) => event.stopPropagation()}><strong>Etiqueta</strong><button onClick={() => { actions.label(card, null); setLabelOpen(false); }}>Sem etiqueta</button>{actions.labels.map((item) => <button key={item.id} onClick={() => { actions.label(card, item.id); setLabelOpen(false); }}><i style={{ background: item.color }} />{item.description}</button>)}</div>
          )}

          {statusOpen && (
            <div className="new-home-card-popover new-home-status-popover" onClick={(event) => event.stopPropagation()}>
              <div className="new-home-status-popover-heading"><small>Status atual</small><strong>{statusLabel}</strong></div>
              {actions.canResetStatus && <button className={!currentStatus ? 'is-current' : ''} onClick={() => run(() => actions.status(card, null))}>
                <span className="new-home-status-option-icon is-default"><MdOutlineTaskAlt /></span>
                <span><b>Aberto</b><small>Em negociação</small></span>
              </button>}
              <button className={`is-success ${currentStatus === 'Vendido' ? 'is-current' : ''}`} disabled={!actions.canStatus} onClick={() => run(() => actions.status(card, 'Vendido'))}>
                <span className="new-home-status-option-icon"><MdThumbUp /></span>
                <span><b>Vendido</b><small>Negociação concluída</small></span>
              </button>
              <button className={`is-danger ${currentStatus === 'Perdido' ? 'is-current' : ''}`} disabled={!actions.canStatus} onClick={() => run(() => actions.status(card, 'Perdido'))}>
                <span className="new-home-status-option-icon"><MdThumbDown /></span>
                <span><b>Perdido</b><small>Negociação perdida</small></span>
              </button>
            </div>
          )}

          {menuOpen && menuPortalTarget && createPortal((
            <>
              <button className="new-home-card-menu-scrim" type="button" aria-label="Fechar menu do card" onClick={() => setMenuOpen(false)} />
              <div className="new-home-card-popover new-home-card-menu is-portal" style={menuPosition || undefined} onClick={(event) => event.stopPropagation()} role="menu">
              <div className="new-home-card-menu-header">
                <span>Card #{card.card_id}</span>
                <strong title={card.name || 'Cliente sem nome'}>{card.name || 'Cliente sem nome'}</strong>
              </div>
              <div className="new-home-card-menu-group">
                <span>Ações</span>
                {actions.canEdit && <button onClick={() => run(() => actions.open('edit', card))}><MdEdit />Editar</button>}
                {actions.canHistory && <button onClick={() => run(() => actions.open('history', card))}><MdHistory />Histórico</button>}
                {actions.canTasks && <button onClick={() => run(() => actions.open('tasks', card))}><MdOutlineTaskAlt />Tarefas</button>}
                {actions.canShare && <button onClick={() => run(() => actions.open('share', card))}><MdShare />Compartilhar</button>}
              </div>
              <div className="new-home-card-menu-group">
                <span>Contato e documentos</span>
                {actions.canContact && <button onClick={() => run(() => actions.chat(card))}><MdForum />Chat interno</button>}
                {actions.canContact && <button onClick={() => run(() => actions.whatsapp(card))}><MdWhatsapp />WhatsApp</button>}
                <button onClick={() => run(() => actions.open('documents', card))}><MdDescription />Documentos</button>
                {actions.canAttachments && <button onClick={() => run(() => actions.open('attachments', card))}><MdAttachFile />Anexos</button>}
                <button onClick={() => run(() => actions.open('email', card))}><MdEmail />E-mail</button>
              </div>
              <div className="new-home-card-menu-group">
                <span>Processo</span>
                {actions.canProduction && <button onClick={() => run(() => actions.open('production', card))}><MdBusiness />Produção</button>}
                {actions.canOrders && <button onClick={() => run(() => actions.open('orders', card))}><MdShoppingCart />Pedidos</button>}
                <button onClick={() => run(() => actions.tracking(card))}><MdLink />Copiar acompanhamento</button>
                {Boolean(card.card_original_id) && <button onClick={() => run(() => actions.original(card))}><MdHistory />Abrir card original</button>}
                {actions.canProductionStage && <button onClick={() => run(() => actions.productionStage(card))}><MdPrecisionManufacturing />Criar etapa de produção</button>}
                {actions.canFaktory && <button onClick={() => run(() => actions.faktory(card))}><MdBusiness />Abrir Faktory</button>}
              </div>
              {(actions.canLock || actions.canDelete) && <div className="new-home-card-menu-group is-management">
                <span>Gerenciamento</span>
                {actions.canLock && <button onClick={() => run(() => actions.lock(card, !card.block_column))}>{card.block_column ? <MdLockOpen /> : <MdLock />}{card.block_column ? 'Desbloquear' : 'Bloquear'}</button>}
                {actions.canDelete && <button className="is-danger" onClick={() => run(() => actions.remove(card))}><MdDeleteOutline />Excluir</button>}
              </div>}
              </div>
            </>
          ), menuPortalTarget)}
        </article>
      )}
    </Draggable>
  );
}

export default React.memo(NewKanbanCard);
